import { Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { keyBy, pick, subtract } from 'lodash/fp';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type PromoCodeFormValues } from '.';
import * as permissionKind from '../../../../../shared/permissions';
import { type PromoCodeDataFragment } from '../../../../api/fragments/PromoCodeData';
import { useGetPromoCodeModuleOptionsQuery } from '../../../../api/queries/getPromoCodeModuleOptions';
import { ModuleType, PromoTypeEnum } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import DealerSelectField from '../../../../components/fields/DealerSelectField';
import InputGroup from '../../../../components/fields/InputGroup';
import ModuleSelectField from '../../../../components/fields/ModuleSelectField';
import SelectField from '../../../../components/fields/SelectField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import hasPermissions from '../../../../utilities/hasPermissions';
import usePromoCodeOptions from '../../../../utilities/usePromoCodeOptions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import FinderVehicleAssignmentsSection from '../../../shared/FinderVehicleAssignmentsSection';
import VehicleAssignmentsSection from '../../../shared/VehicleAssignmentsSection';

export type EditPromoCodeFormProps = {
    updated: string;
    promoCode: PromoCodeDataFragment;
    offset: string;
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const EditPromoCodeForm = ({ updated, promoCode, offset }: EditPromoCodeFormProps) => {
    const { t } = useTranslation(['promoCodeDetails']);
    const { initialValues, values, setFieldValue } = useFormikContext<PromoCodeFormValues>();
    const company = useCompany(true);
    const { promoCodeTypeOptions, discountTypeOptions } = usePromoCodeOptions(promoCode?.module?.company?.currency);
    const { yesNoSwitch } = useSystemSwitchData();
    const { data: modulesOptions } = useGetPromoCodeModuleOptionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                moduleTypes: [
                    ModuleType.SimpleVehicleManagement,
                    ModuleType.FinderVehicleManagement,
                    ModuleType.EventApplicationModule,
                    ModuleType.FinderApplicationPublicModule,
                    ModuleType.FinderApplicationPrivateModule,
                    ModuleType.StandardApplicationModule,
                    ModuleType.ConfiguratorModule,
                    ModuleType.MobilityModule,
                ],
                companyId: promoCode.module.companyId,
            },
        },
    });

    const [vehicleModulesData, vehicleModulesMapping] = useMemo(() => {
        const modulesData = modulesOptions?.modules?.items.filter(
            module =>
                module.__typename.includes(ModuleType.SimpleVehicleManagement) ||
                module.__typename.includes(ModuleType.FinderVehicleManagement)
        );
        const modulesMapping = keyBy('id', modulesData);

        return [modulesData, modulesMapping];
    }, [modulesOptions?.modules?.items]);

    const [applicationModulesData, applicationModulesMapping] = useMemo(() => {
        const modulesData = modulesOptions?.modules?.items.filter(module => {
            if (
                module.__typename === 'StandardApplicationModule' ||
                module.__typename === 'ConfiguratorModule' ||
                module.__typename === 'FinderApplicationPublicModule' ||
                module.__typename === 'FinderApplicationPrivateModule' ||
                module.__typename === 'MobilityModule'
            ) {
                return module.promoCodeModuleId === promoCode.moduleId;
            }

            return false;
        });

        const modulesMapping = keyBy('id', modulesData);

        return [modulesData, modulesMapping];
    }, [modulesOptions?.modules?.items, promoCode.moduleId]);

    //  application modules except Mobility
    const hasApplicationModules = useMemo(() => {
        if (!values.applicationModuleIds.length) {
            return false;
        }

        return values.applicationModuleIds.some(moduleId =>
            [
                'StandardApplicationModule',
                'EventApplicationModule',
                'ConfiguratorModule',
                'FinderApplicationPublicModule',
                'FinderApplicationPrivateModule',
                'MobilityModule',
            ].includes(applicationModulesMapping[moduleId]?.__typename)
        );
    }, [applicationModulesMapping, values.applicationModuleIds]);

    const hasUpdatePermission = hasPermissions(promoCode.permissions, [permissionKind.updatePromoCode]);

    useEffect(() => {
        setFieldValue('moduleVariantsMapping', pick(values.variantModuleIds, values.moduleVariantsMapping));

        // Currently ignore it, as it's become infinite loop if we add "values.moduleVariantsMapping"
        // and "setFieldValue" to the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.variantModuleIds]);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('promoCodeDetails:cardTitles.promoCodeDetails')}>
                <Panel
                    key={t('promoCodeDetails:cardTitles.promoCodeDetails')}
                    header={t('promoCodeDetails:cardTitles.promoCodeDetails')}
                >
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('promoCodeDetails:fields.code', { returnObjects: true })}
                                disabled={!hasUpdatePermission}
                                name="promoCode"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.RangePickerField
                                {...t('promoCodeDetails:fields.period', { returnObjects: true })}
                                disabled={!hasUpdatePermission}
                                name="period"
                                required
                            />
                        </Col>

                        <Col {...colSpan}>
                            <FormFields.TimePickerField
                                {...t('promoCodeDetails:fields.startTime', { returnObjects: true })}
                                disabled={!hasUpdatePermission}
                                name="startTime"
                                offset={offset}
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TimePickerField
                                {...t('promoCodeDetails:fields.endTime', { returnObjects: true })}
                                disabled={!hasUpdatePermission}
                                name="endTime"
                                offset={offset}
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.DisplayField
                                {...t('promoCodeDetails:fields.module', { returnObjects: true })}
                                disabled={!hasUpdatePermission}
                                value={promoCode.module.displayName}
                            />
                        </Col>
                        {hasApplicationModules && (
                            <Col {...colSpan}>
                                <DealerSelectField
                                    {...t('promoCodeDetails:fields.dealers', { returnObjects: true })}
                                    companyId={promoCode.module.companyId}
                                    disabled={!hasUpdatePermission}
                                    forceSkipFetch={!promoCode.module.companyId}
                                    mode="multiple"
                                    name="dealerIds"
                                />
                            </Col>
                        )}
                        <Col {...colSpan}>
                            <ModuleSelectField
                                {...t('promoCodeDetails:fields.applicationModule', { returnObjects: true })}
                                autoSelected={false}
                                companyId={company?.id}
                                disabled={!hasUpdatePermission}
                                mode="multiple"
                                modules={applicationModulesData}
                                name="applicationModuleIds"
                                required
                            />
                        </Col>
                        {hasApplicationModules && (
                            <Col {...colSpan}>
                                <ModuleSelectField
                                    {...t('promoCodeDetails:fields.vehicleModule', { returnObjects: true })}
                                    autoSelected={false}
                                    companyId={promoCode.module.companyId || company?.id}
                                    disabled={!hasUpdatePermission}
                                    mode="multiple"
                                    modules={vehicleModulesData}
                                    name="variantModuleIds"
                                />
                            </Col>
                        )}
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('promoCodeDetails:fields.type', { returnObjects: true })}
                                disabled={!hasUpdatePermission}
                                name="promoType.type"
                                options={promoCodeTypeOptions}
                                required
                                showSearch
                            />
                        </Col>
                        {values.promoType.type === PromoTypeEnum.Discount && (
                            <>
                                <Col {...colSpan}>
                                    <InputGroup
                                        {...t('promoCodeDetails:fields.discountAmount', { returnObjects: true })}
                                        required
                                    >
                                        <FormFields.InputNumberField
                                            addonAfter={
                                                <SelectField
                                                    itemProps={{ noStyle: true }}
                                                    name="promoType.discountPromoType.discountType"
                                                    options={discountTypeOptions}
                                                />
                                            }
                                            disabled={!hasUpdatePermission}
                                            name="promoType.discountPromoType.amount"
                                        />
                                    </InputGroup>
                                </Col>
                                {hasApplicationModules && (
                                    <Col {...colSpan}>
                                        <FormFields.SwitchField
                                            {...t('promoCodeDetails:fields.deductFromFinancing', {
                                                returnObjects: true,
                                            })}
                                            {...yesNoSwitch}
                                            defaultChecked={
                                                initialValues.promoType?.discountPromoType?.deductFromFinancing
                                            }
                                            disabled={!hasUpdatePermission}
                                            name="promoType.discountPromoType.deductFromFinancing"
                                        />
                                    </Col>
                                )}
                            </>
                        )}
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('promoCodeDetails:fields.description', { returnObjects: true })}
                                disabled={!hasUpdatePermission}
                                name={`promoType.${
                                    values.promoType.type === PromoTypeEnum.Discount
                                        ? 'discountPromoType'
                                        : 'giftPromoType'
                                }.description`}
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('promoCodeDetails:fields.trackUsage', {
                                    returnObjects: true,
                                })}
                                {...yesNoSwitch}
                                defaultChecked={initialValues.trackUsage}
                                disabled={!hasUpdatePermission}
                                name="trackUsage"
                            />
                        </Col>
                        {values.trackUsage && (
                            <>
                                <Col {...colSpan}>
                                    <FormFields.InputNumberField
                                        {...t('promoCodeDetails:fields.initialValue', { returnObjects: true })}
                                        disabled={!hasUpdatePermission}
                                        name="initialQuantity"
                                        required
                                    />
                                </Col>
                                <Col {...colSpan}>
                                    <FormFields.DisplayField
                                        {...t('promoCodeDetails:fields.remainingValue', { returnObjects: true })}
                                        disabled={!hasUpdatePermission}
                                        value={subtract(values.initialQuantity || 0, promoCode.usedValue)}
                                    />
                                </Col>
                            </>
                        )}
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('promoCodeDetails:fields.active', {
                                    returnObjects: true,
                                })}
                                {...yesNoSwitch}
                                defaultChecked={initialValues.isActive}
                                disabled={!hasUpdatePermission}
                                name="isActive"
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.DisplayFieldWithUTCOffset
                                {...t('common:fields.updated', { returnObjects: true, offset })}
                                value={updated}
                            />
                        </Col>
                    </Row>
                </Panel>
            </CollapsibleWrapper>

            {hasApplicationModules &&
                values?.variantModuleIds?.length > 0 &&
                values?.variantModuleIds?.map((moduleId, index) => {
                    const currentModule = vehicleModulesMapping[moduleId];
                    if (currentModule?.__typename === 'SimpleVehicleManagementModule') {
                        return (
                            <VehicleAssignmentsSection
                                key={moduleId}
                                filter={{ moduleId }}
                                header={t('promoCodeDetails:tabs.panel.vehicleAssignment', {
                                    moduleName: currentModule?.displayName,
                                })}
                                name={`moduleVariantsMapping.${[moduleId]}`}
                                transferFieldProps={{ disabled: !hasUpdatePermission }}
                            />
                        );
                    }

                    if (company?.id && currentModule?.__typename === 'FinderVehicleManagementModule') {
                        return (
                            <FinderVehicleAssignmentsSection
                                key={moduleId}
                                allowSync={hasPermissions(company?.permissions, [permissionKind.manageFinderVehicles])}
                                filter={{ moduleId, companyId: company?.id }}
                                header={t('promoCodeDetails:tabs.panel.vehicleAssignment', {
                                    moduleName: currentModule?.displayName,
                                })}
                                name={`moduleVariantsMapping.${[moduleId]}`}
                                transferFieldProps={{ disabled: !hasUpdatePermission }}
                            />
                        );
                    }

                    return null;
                })}
        </Space>
    );
};

export default EditPromoCodeForm;
